<template>
  <v-dialog v-model="show" max-width="733">
    <v-card v-if="data">
      <v-card-title>{{ data.name }}</v-card-title>
      <v-card-subtitle
        >{{ data.email }} - {{ data.employeeLanguage }}</v-card-subtitle
      >

      <v-card-text>
        <div class="employee-info-row">
          <v-card flat>
            <v-card-title>{{
              $t("employeeDetailsDialog.createdAt")
            }}</v-card-title>
            <v-card-subtitle class="text-body-1">{{
              formatDate(new Date(data.createdAt))
            }}</v-card-subtitle>
          </v-card>
          <v-card flat>
            <v-card-title>{{
              $t("employeeDetailsDialog.changed")
            }}</v-card-title>
            <v-card-subtitle class="text-body-1">{{
              formatDate(new Date(data.createdAt))
            }}</v-card-subtitle>
          </v-card>
          <v-card flat>
            <v-card-title>{{
              $t("employeeDetailsDialog.overdueInstructions")
            }}</v-card-title>
            <v-card-subtitle
              v-bind:class="{ 'red--text': overdueAssignments?.length > 0 }"
              class="text-body-1 font-weight-bold"
            >
              {{ overdueAssignments?.length }}
            </v-card-subtitle>
          </v-card>
        </div>
        <v-tabs v-model="tab">
          <v-tab>{{ $t("employee.assignedTab") }}</v-tab>
          <v-tab>{{ $t("employee.historyTab") }}</v-tab>
          <v-tab v-if="!isExternal">{{ $t("employee.organizationTab") }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-data-table
              :headers="headers"
              :items="instructionAssignments"
              :options.sync="tableDataGetter.pagination"
              :footer-props="{
                itemsPerPageOptions:
                  tableDataGetter.pagination.rowsPerPageItems,
                itemsPerPageText: $t('general.rowsPerPage'),
              }"
            >
              <template v-slot:item.instruction="{ item }">
                {{ getInstructionName(item.managedInstruction) }}
              </template>
              <template v-slot:item.status="{ item }">
                {{
                  $t(
                    `managedInstructionAssignment.assignmentStatus.${item.status}`,
                  )
                }}
              </template>
            </v-data-table>
          </v-tab-item>
          <v-tab-item>
            <div
              v-if="!overdueAssignments?.length && !finishedAssignments?.length"
            >
              <h3 class="text--primary mb-2">
                {{ $t("employeeDetailsDialog.noInstructions") }}
              </h3>
            </div>
            <div v-if="overdueAssignments?.length > 0">
              <!-- <v-divider class="my-4" /> -->
              <h3 class="text--primary my-2">
                {{ $t("employeeDetailsDialog.overdueInstructions") }}
              </h3>
              <v-card outlined>
                <v-expansion-panels flat>
                  <v-expansion-panel
                    v-for="assignment in overdueAssignments"
                    v-bind:key="assignment['_id']"
                  >
                    <v-expansion-panel-header
                      >{{ getInstructionName(assignment.instruction) }}
                      {{ formatDate(new Date(assignment.dueDate)) }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-divider class="mb-4" />

                      <span class="text-lg-caption font-weight-bold">{{
                        $t("employeeDetailsDialog.createdAt")
                      }}</span>
                      <p>
                        {{ formatDate(new Date(assignment.createdAt)) }}
                      </p>

                      <span class="text-lg-caption font-weight-bold">{{
                        $t("employeeDetailsDialog.dueTo")
                      }}</span>
                      <p>
                        {{ formatDate(new Date(assignment.dueDate)) }}
                      </p>

                      <div class="d-flex justify-end">
                        <managed-instruction-assignment-overdue-send-reminder
                          :employee="data"
                          :instruction-id="assignment._id"
                        />
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card>
            </div>
            <div v-if="finishedAssignments?.length > 0">
              <v-divider class="my-4" />
              <h3 class="text--primary mb-2">
                {{ $t("employeeDetailsDialog.completedInstructions") }}
              </h3>
              <v-card outlined>
                <v-expansion-panels flat>
                  <v-expansion-panel
                    v-for="assignment in finishedAssignments"
                    v-bind:key="assignment['_id']"
                  >
                    <v-expansion-panel-header
                      >{{ getInstructionName(assignment.managedInstruction) }}
                      {{ formatDate(new Date(assignment.dueDate)) }}
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                      <v-divider class="mb-4" />

                      <span class="text-lg-caption font-weight-bold">{{
                        $t("employeeDetailsDialog.createdAt")
                      }}</span>
                      <p>{{ formatDate(new Date(assignment.createdAt)) }}</p>

                      <span class="text-lg-caption font-weight-bold">{{
                        $t("employeeDetailsDialog.dueTo")
                      }}</span>
                      <p>{{ formatDate(new Date(assignment.dueDate)) }}</p>

                      <span class="text-lg-caption font-weight-bold">{{
                        $t("employeeDetailsDialog.completedAt")
                      }}</span>
                      <p>
                        {{
                          assignment?.instruction
                            ? formatDate(new Date(assignment.updatedAt))
                            : formatDate(new Date(assignment.finishedAt))
                        }}
                      </p>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card>
            </div>
          </v-tab-item>
          <v-tab-item v-if="!isExternal">
            <div class="pt-3">
              <div
                v-if="!assignedRoles.length"
                class="pa-4 text--secondary text-body-2"
              >
                {{ $t("employeeDetailsDialog.noRoles") }}
              </div>

              <v-list v-else two-line class="py-0">
                <v-list-item
                  v-for="role in assignedRoles"
                  :key="role.key"
                  class="org-role-item"
                >
                  <v-list-item-avatar>
                    <v-icon>{{ role.icon }}</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ role.label }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <template v-if="role.nextDate">
                        {{ $t("employeeDetailsDialog.validUntil") }}:
                        <span :class="dateClass(role.nextDate)">
                          {{ formatDate(role.nextDate) }}
                        </span>
                      </template>
                      <template v-else>
                        {{ $t("employeeDetailsDialog.noExpiry") }}
                      </template>
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-chip
                      small
                      outlined
                      :color="role.overdue ? 'error' : 'success'"
                    >
                      <v-icon left small>
                        {{ role.overdue ? "mdi-clock" : "mdi-check-circle" }}
                      </v-icon>
                      {{
                        role.overdue
                          ? $t("employee.roleOverdue")
                          : $t("employeeDetailsDialog.valid")
                      }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text color="primary" v-on:click="show = false">{{
          $t("general.back")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import axios from "axios";
import { dateUtil } from "@/components/api/api";
import ManagedInstructionAssignmentOverdueSendReminder from "../../../views/managedInstruction/managedInstructionAssignmentOverdueSendReminder.vue";
import { mapGetters } from "vuex";

const BUILT_IN_ROLES = {
  safetyOfficer: { icon: "mdi-shield-account", label: "employee.safetyOfficer" },
  fireMarshal: { icon: "mdi-fire", label: "employee.fireMarshal" },
  firstAider: { icon: "local_hospital", label: "employee.firstAider" },
};

export default {
  components: {
    ManagedInstructionAssignmentOverdueSendReminder,
  },
  props: {
    value: Boolean,
  },
  computed: {
    ...mapGetters(["tableDataGetter"]),
    headers() {
      return [
        {
          text: this.$t("instructions.instruction"),
          value: "instruction",
          align: "left",
        },
        {
          text: this.$t("general.status"),
          value: "status",
        },
      ];
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    isExternal() {
      return !!(this.data && this.data.external);
    },
    // Only the roles this employee actually has, resolved to a display
    // descriptor (built-in roles + custom role definitions).
    assignedRoles() {
      const defs = this.$store.getters.employeeRoleDefinitions || [];
      return (this.orgRoles || [])
        .filter((entry) => BUILT_IN_ROLES[entry.role] || defs.some((d) => d._id === entry.role))
        .map((entry) => {
        const key = entry.role;
        const builtIn = BUILT_IN_ROLES[key];
        let icon;
        let label;
        if (builtIn) {
          icon = builtIn.icon;
          label = this.$t(builtIn.label);
        } else {
          const def = defs.find((d) => d._id === key);
          icon = def?.icon || "mdi-account-star";
          label = def?.name || key;
        }
        const nextDate = entry.nextDate ? new Date(entry.nextDate) : null;
        return {
          key,
          icon,
          label,
          nextDate,
          overdue: nextDate ? this.isPast(nextDate) : false,
        };
      });
    },
  },
  data: () => ({
    data: undefined,
    overdueAssignments: undefined,
    finishedAssignments: undefined,
    instructionAssignments: undefined,
    orgRoles: [],
    tab: 0,
  }),
  watch: {
    show(newVal) {
      if (!newVal) {
        this.data = undefined;
        this.overdueAssignments = undefined;
        this.finishedAssignments = undefined;
        this.orgRoles = [];
      } else {
        this.fetchData();
      }
    },
  },
  methods: {
    getInstructionName(instruction) {
      const names = instruction?.name ?? [];
      const employeeLang = this.data?.employeeLanguage;

      const langMap = {
        ENG: "EN",
        GER: "GER",
      };

      const targetLang = langMap[employeeLang] ?? employeeLang;

      const matched = names.find((x) => x?.language === targetLang && x?.name);

      return matched?.name ?? "";
    },
    formatDate(date) {
      if (!date || isNaN(date)) return null;
      return dateUtil(new Date(date), true, "ERROR");
    },
    fetchData() {
      this._fetchOverdue();
      this._fetchFinishedAssignments();
      this._fetchGroups();
      this._fetchOrganisation();
      // Ensure custom role definitions are available to resolve names/icons.
      this.$store.dispatch("getEmployeeRoleDefinitions");
    },
    _fetchOverdue() {
      axios
        .get(`/employee/${this.data["_id"]}/overdueAssignments`)
        .then((rawData) => (this.overdueAssignments = rawData.data.data));
    },
    _fetchFinishedAssignments() {
      axios
        .get(`/employee/${this.data["_id"]}/finishedAssignments`)
        .then((rawData) => (this.finishedAssignments = rawData.data.data));
    },
    _fetchGroups() {
      axios
        .get(`/employee/${this.data["_id"]}/allInstructions`)
        .then((rawData) => {
          this.instructionAssignments = rawData.data.data;
        });
    },
    _fetchOrganisation() {
      axios.get(`/employee/${this.data["_id"]}`).then((rawData) => {
        this.orgRoles = rawData.data?.roles || [];
      });
    },
    isPast(date) {
      if (!date) return false;
      const d = new Date(date);
      if (isNaN(d)) return false;
      const today = new Date();
      return d < today;
    },
    dateClass(date) {
      return {
        "red--text": this.isPast(date),
      };
    },
    // extractUniqueInstructionAssignments(data) {
    //   const uniqueInstructions = {};

    //   data.forEach((group) => {
    //     const groupName = group.name;

    //     group.instructionAssignments.forEach((assignment) => {
    //       const instructionId = assignment.instruction._id;

    //       if (!uniqueInstructions[instructionId]) {
    //         uniqueInstructions[instructionId] = {
    //           ...assignment,
    //           groups: [groupName], // Initialize groups with the current group name
    //         };
    //       } else {
    //         uniqueInstructions[instructionId].groups.push(groupName);
    //       }
    //     });
    //   });

    //   return Object.values(uniqueInstructions);
    // },
  },
};
</script>

<style scoped>
.employee-info-row {
  display: flex;
  flex-direction: row;
  flex-shrink: 0;

  flex-wrap: wrap;

  gap: 15px;

  flex-grow: 1;

  justify-content: space-between;
}

.employee-info-row * {
  padding-left: 0;
  padding-right: 0;
}
</style>
